import {
  useCallback,
  useEffect,
  useRef,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from "react";

const ITEM_SELECTOR = "[data-toolbar-item]:not([disabled])";

/**
 * Roving tabindex for a `role="toolbar"` container, per the WAI-ARIA Authoring Practices:
 * the toolbar is a single tab stop and the arrow keys move focus between its buttons.
 *
 * Buttons are resolved from the DOM on every render and on every key press rather than being
 * registered by the caller, because the set changes while the editor is used — the table
 * operations only exist while the cursor sits inside a table, and buttons come and go with the
 * toolbar config.
 */
export const useRovingToolbar = ({ onEscape }: { onEscape?: () => void } = {}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const activeIndex = useRef(0);

  const onEscapeRef = useRef(onEscape);
  useEffect(() => {
    onEscapeRef.current = onEscape;
  });

  const items = useCallback(
    () => Array.from(containerRef.current?.querySelectorAll<HTMLElement>(ITEM_SELECTOR) ?? []),
    []
  );

  const syncTabIndexes = useCallback(() => {
    const buttons = items();
    if (buttons.length === 0) return;
    activeIndex.current = Math.min(activeIndex.current, buttons.length - 1);
    buttons.forEach((button, index) => {
      button.tabIndex = index === activeIndex.current ? 0 : -1;
    });
  }, [items]);

  // No dependency array: the button set can change on any render, and the index is clamped when
  // it shrinks.
  useEffect(syncTabIndexes);

  const focusAt = useCallback(
    (index: number) => {
      const buttons = items();
      if (buttons.length === 0) return;
      const next = ((index % buttons.length) + buttons.length) % buttons.length;
      activeIndex.current = next;
      syncTabIndexes();
      buttons[next].focus();
    },
    [items, syncTabIndexes]
  );

  const focusCurrent = useCallback(() => focusAt(activeIndex.current), [focusAt]);

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      if (!(event.target as HTMLElement).closest(ITEM_SELECTOR)) return;
      // A modified key press means something else to the browser or the OS; it is not toolbar
      // navigation.
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
          focusAt(activeIndex.current + 1);
          break;
        case "ArrowLeft":
        case "ArrowUp":
          focusAt(activeIndex.current - 1);
          break;
        case "Home":
          focusAt(0);
          break;
        case "End":
          focusAt(items().length - 1);
          break;
        case "Escape":
          // With nowhere to send focus, swallowing Escape would trap the user inside whatever
          // modal encloses the toolbar.
          if (!onEscapeRef.current) return;
          onEscapeRef.current();
          break;
        default:
          return;
      }

      event.preventDefault();
      // Escape in particular must not reach an enclosing modal or drawer — leaving the toolbar is
      // not a request to close what contains it.
      event.stopPropagation();
    },
    [focusAt, items]
  );

  // Clicking or tabbing to a button makes it the one the toolbar returns to.
  const onFocus = useCallback(
    (event: ReactFocusEvent) => {
      const item = (event.target as HTMLElement).closest<HTMLElement>(ITEM_SELECTOR);
      if (!item) return;
      const index = items().indexOf(item);
      if (index === -1) return;
      activeIndex.current = index;
      syncTabIndexes();
    },
    [items, syncTabIndexes]
  );

  // Every button runs an editor command that pulls focus back into the text. That is what a mouse
  // user wants, but a keyboard user was standing in the toolbar and should still be — Escape is
  // the way out, not Enter. A keyboard activation is a click with no pointer behind it.
  const pendingRefocus = useRef<number>();
  const onClick = useCallback(
    (event: ReactMouseEvent) => {
      if (event.detail !== 0) return;
      if (!(event.target as HTMLElement).closest(ITEM_SELECTOR)) return;
      // Wait for the frame the command re-renders in: the button may have just been disabled
      // (inserting a table disables «insert table»), and by then the index has been clamped onto
      // one that can still hold focus.
      if (pendingRefocus.current !== undefined) cancelAnimationFrame(pendingRefocus.current);
      pendingRefocus.current = requestAnimationFrame(focusCurrent);
    },
    [focusCurrent]
  );

  useEffect(() => () => cancelAnimationFrame(pendingRefocus.current ?? 0), []);

  return { containerRef, focusCurrent, toolbarHandlers: { onKeyDown, onFocus, onClick } };
};

/**
 * Tracks whether focus is anywhere inside an element. Moving between two buttons blurs one and
 * focuses the next, so only a blur that lands outside counts as leaving.
 */
export const focusWithinHandlers = ({ onChange }: { onChange: (focusWithin: boolean) => void }) => ({
  onFocus: (event: ReactFocusEvent) => onChange(event.currentTarget.contains(event.target)),
  onBlur: (event: ReactFocusEvent) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    onChange(false);
  },
});
